import * as Sentry from '@sentry/react';
import {
  doc,
  getDoc,
  runTransaction,
  Timestamp,
  type Transaction,
  updateDoc,
} from 'firebase/firestore';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { uid } from 'uid';

import { GlobalSection } from '@superfunnel/types/global-section';
import type { PopupData } from '@superfunnel/types/popup';
import { Website, WebsiteData } from '@superfunnel/types/website';
import AlertModal from '@superfunnel/ui-core/AlertModal';
import { getCMSRefsInWebsiteData } from '@superfunnel/utils/content-library';
import { getFormIdsInWebsiteData } from '@superfunnel/utils/form';
import { groupBy } from '@superfunnel/utils/array';

import { db } from '../Firebase';
import { publishGlobalSection } from '../services/GlobalSectionsService';
import { emptyConverter, sendMessageToIframeByWebsiteId } from '../utils';
import {
  classifySaveFailure,
  getRetryDelay,
  markSaving,
  type SaveFailure,
  type SaveStatus,
} from '../utils/save-retry';
import { track } from '../utils/analytics';
import { LimitedStack } from '../utils/limited-stack';
import { useFlushSaves } from '../hooks/useFlushSaves';
import { removeUndefinedFieldsDeep } from '../utils/firestore';
import { revertTouched } from '../utils/revert-touched';
import { getSaveDelay } from '../utils/save-delay';
import { useSnackbarContext } from './SnackbarContext';
import { useSnapshotCapture } from '../hooks/useSnapshotCapture';
import { useAuditLog } from './AuditLogContext';
import { UserContext } from './UserContext';
import {
  applyChange as applyChangeToWebsite,
  getElementInnerHtml,
} from './website-updater/apply-website-changes';

export function getItemFromSection(
  section: any,
  propKey?: string,
  subitems?: { id: string; propKey: string; type: 'array' }[] | undefined,
) {
  let itemFound: any = section;

  if (subitems?.length) {
    subitems.forEach((item) => {
      if (item.type === 'array') {
        itemFound = itemFound?.[item.propKey]?.find(
          (it: any) => it.id === item.id,
        );
      } else {
        throw new Error('unknown subitem type: ' + item.type);
      }
    });
    if (!itemFound) return;
  }

  if (!propKey) return itemFound;
  const propKeyParts = propKey.split('.');
  propKeyParts.forEach((propKeyPart) => {
    if (!itemFound) return;

    itemFound = itemFound[propKeyPart];
  });

  return itemFound;
}

export type WebsiteUpdateType =
  | 'text'
  | 'htmlElementChanged'
  | 'changeDesign'
  | 'addSectionAbove'
  | 'addSectionBelow'
  | 'moveSectionUp'
  | 'moveSectionDown'
  | 'reorderSections'
  | 'deleteSection'
  | 'duplicateSection'
  | 'deleteSectionItem'
  | 'editSectionItem'
  | 'duplicateSectionItem'
  | 'addSectionItem'
  | 'editButton'
  | 'editHtmlAction'
  | 'editHtmlVariable'
  | 'editCmsListSettings'
  | 'editCmsEntryMode'
  | 'editSection'
  | 'editForm'
  | 'replaceForm'
  | 'editImageSettings'
  | 'selectImage'
  | 'selectThumbnail'
  | 'revertChange'
  | 'discard'
  | 'restoreSnapshot'
  | 'resetConfig'
  | 'resetBrand'
  | 'editBrand'
  | 'editConfig'
  | 'convertGlobalToLocal'
  | 'convertLocalToGlobal'
  | 'customValueMentionClick'
  // Apply one accepted agent change from its own `SessionChangeContent`, so a
  // burst of accepts folds against a single fresh read like every other update
  // type instead of each one replacing the whole document.
  | 'agentChange'
  // HTML list operations
  | 'deleteHtmlListItem'
  | 'duplicateHtmlListItem'
  | 'moveHtmlListItem'
  | 'toggleHtmlListItemHidden'
  // Popup update types
  | 'addPopup'
  | 'updatePopup'
  | 'deletePopup';

type UpdateWebsiteFn = (wu: WebsiteUpdate) => void;

type CreateUpdateWebsiteFn = (
  dynamicPageId: string,
  websiteId: string,
  sectionId: string,
  propKey: string,
  content: any,
  type: WebsiteUpdateType,
  subitems?: { id: string; propKey: string; type: 'array' }[],
  before?: WebsiteData,
) => WebsiteUpdate | undefined;

export type WebsiteUpdate = {
  id: string;
  accountId: string;
  campaignId: string;
  websiteId: string;
  sectionId: string;
  propKey: string;
  content: any;
  type: WebsiteUpdateType;
  subitems?: { id: string; propKey: string; type: 'array' }[];
  before?: WebsiteData;
  /** Extra writes that must commit in the same transaction as this update. */
  stageOnCommit?: (transaction: Transaction) => void;
  /** An element edit's inner HTML as this tab saw it before the edit. */
  baseContent?: string;
  /** A popup update's popup as the panel showed it before the change. */
  basePopup?: PopupData;
  /** Set when a newer change to the same text kept this edit from applying. */
  conflicted?: boolean;
};

type CurrentDevelopmentGetter = (websiteId: string) => WebsiteData | undefined;

export interface WebsiteUpdaterContextProps {
  saveStatus: SaveStatus;
  setSaveStatus: (_: SaveStatus) => void;
  lastSavedAt: Date | null;

  publishing: boolean;
  publish: (dynamicPageId: string, websiteId: string) => Promise<any>;

  discarding: boolean;
  discardChanges: (dynamicPageId: string, websiteId: string) => Promise<any>;

  createWebsiteUpdate: CreateUpdateWebsiteFn;
  updateWebsite: UpdateWebsiteFn;

  canRevert: boolean;
  canForward: boolean;
  revertLatestUpdate: (getCurrent?: CurrentDevelopmentGetter) => boolean;
  forwardRevert: (getCurrent?: CurrentDevelopmentGetter) => boolean;
  clearHistory: () => void;

  applyChangesSync: (
    websiteId: string,
    development: WebsiteData,
    extra?: WebsiteUpdate[],
  ) => any;

  flushSnapshots: () => void;
  flushPendingSaves: () => Promise<void>;
  waitForSaved: (updateIds: string[]) => Promise<void>;
}

// True while the live copy of the element an edit saved to still shows what it
// had before that edit: the page has not caught up and nobody changed it since.
const isLiveElementUnchanged = (wu: WebsiteUpdate, live: WebsiteData) => {
  if (!wu.before) return false;
  const current = getElementInnerHtml(live, wu.sectionId, wu.propKey);
  return (
    current !== undefined &&
    current === getElementInnerHtml(wu.before, wu.sectionId, wu.propKey)
  );
};
const RECENTLY_SAVED_REPLAY_MS = 5000;

export const WebsiteUpdaterContext = createContext<WebsiteUpdaterContextProps>({
  saveStatus: 'none',
  setSaveStatus: () => {},
  lastSavedAt: null,

  publishing: false,
  publish: async () => {},

  discarding: false,
  discardChanges: async () => {},

  updateWebsite: () => undefined,
  createWebsiteUpdate: () => undefined,

  canRevert: false,
  canForward: false,
  revertLatestUpdate: () => false,
  forwardRevert: () => false,
  clearHistory: () => {},

  applyChangesSync: () => {},

  flushSnapshots: () => {},
  flushPendingSaves: async () => {},
  waitForSaved: async () => {},
});

const getUpdatesTrackingProperties = (updates: WebsiteUpdate[]) => ({
  dynamic_page_id: updates[0]?.campaignId,
  website_id: updates[0]?.websiteId,
  update_types: [...new Set(updates.map((wu) => wu.type))],
  update_count: updates.length,
});

export function WebsiteUpdaterContextProvider(props: any) {
  const { selectedAccount } = useContext(UserContext);
  const { show } = useSnackbarContext();
  const auditLog = useAuditLog();
  const { captureSnapshot, scheduleSnapshot, flushSnapshots } =
    useSnapshotCapture();
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('none');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [updates, setUpdates] = useState<WebsiteUpdate[]>([]);
  const [history, setHistory] = useState<LimitedStack<WebsiteUpdate>>(
    new LimitedStack(20),
  );
  const [revertHistory, setRevertHistory] = useState<
    LimitedStack<WebsiteUpdate>
  >(new LimitedStack(20));
  const [publishGlobalSectionModalOpen, setPublishGlobalSectionModalOpen] =
    useState<{
      websiteId: string;
      globalSectionId: string;
      loading?: boolean;
    } | null>(null);

  const createWebsiteUpdate: CreateUpdateWebsiteFn = useCallback(
    (
      dynamicPageId,
      websiteId,
      sectionId,
      propKey,
      content,
      type,
      subitems,
      before,
    ) => {
      if (!selectedAccount) return;

      const wu: WebsiteUpdate = {
        id: uid(8),
        accountId: selectedAccount.id,
        campaignId: dynamicPageId,
        websiteId,
        sectionId,
        propKey,
        subitems,
        content,
        type,
        before,
      };

      return wu;
    },
    [selectedAccount],
  );

  const updatesRef = useRef(updates);
  updatesRef.current = updates;

  // The ref is updated synchronously too, so a flush right after queueing
  // (an accept, leaving the page) saves the update instead of missing it.
  const enqueue = useCallback((wu: WebsiteUpdate) => {
    updatesRef.current = [...updatesRef.current, wu];
    setUpdates((u) => [...u, wu]);
  }, []);

  const updateWebsite: UpdateWebsiteFn = useCallback(
    (wu: WebsiteUpdate) => {
      if (!selectedAccount) return;

      setSaveStatus(markSaving);
      enqueue(wu);
      if (wu.before) {
        setHistory((h) => {
          const newHistory = new LimitedStack(h);
          newHistory.push(wu);
          return newHistory;
        });
      }
      setRevertHistory(new LimitedStack(20));
    },
    [selectedAccount, enqueue],
  );

  const clearHistory = useCallback(() => {
    setHistory((history) => new LimitedStack(history.getMaxLength()));
    setRevertHistory(
      (revertHistory) => new LimitedStack(revertHistory.getMaxLength()),
    );
  }, []);

  const snapshotSavedEntity = useCallback(
    (accountId: string, entityPath: string, savedUpdates: WebsiteUpdate[]) => {
      if (savedUpdates.some((wu) => wu.type === 'agentChange')) {
        captureSnapshot(accountId, entityPath, 'agent');
        return;
      }
      if (savedUpdates.some((wu) => wu.type === 'restoreSnapshot')) {
        captureSnapshot(accountId, entityPath, 'restore');
        return;
      }
      scheduleSnapshot(accountId, entityPath);
    },
    [captureSnapshot, scheduleSnapshot],
  );

  // Use the extracted applyChange function directly
  const applyChange = useCallback(
    (wu: WebsiteUpdate, development: WebsiteData) => {
      return applyChangeToWebsite(wu, development);
    },
    [],
  );

  // Edits leave the queue when their save starts but reach this tab's copy of
  // the page only when the live document catches up. Until then they are kept
  // here, so a re-render from a remote write (another tab, the agent) still
  // shows them and the next edit to the same element is not sent without them.
  const inFlightUpdatesRef = useRef<WebsiteUpdate[]>([]);
  const recentlySavedRef = useRef<{ wu: WebsiteUpdate; savedAt: number }[]>([]);

  const applyChangesSync = useCallback(
    (websiteId: string, development: WebsiteData, extra?: WebsiteUpdate[]) => {
      const now = Date.now();
      recentlySavedRef.current = recentlySavedRef.current.filter(
        ({ wu, savedAt }) =>
          now - savedAt < RECENTLY_SAVED_REPLAY_MS &&
          isLiveElementUnchanged(wu, development),
      );
      const websiteUpdates = [
        ...recentlySavedRef.current.map(({ wu }) => wu),
        ...inFlightUpdatesRef.current,
        ...updates,
        ...(extra ?? []),
      ].filter((wu) => wu.websiteId === websiteId);

      websiteUpdates.forEach((wu) => applyChange(wu, development));

      return development;
    },
    [applyChange, updates],
  );

  const repaint = useCallback(
    (
      wu: WebsiteUpdate,
      fallback: WebsiteData,
      getCurrent?: CurrentDevelopmentGetter,
    ) => {
      const current = getCurrent?.(wu.websiteId);
      const development = JSON.parse(JSON.stringify(current ?? fallback));
      applyChange(wu, development);
      sendMessageToIframeByWebsiteId(
        { eventName: 'websiteUpdated', website: development },
        wu.websiteId,
      );
    },
    [applyChange],
  );

  const revertLatestUpdate = useCallback(
    (getCurrent?: CurrentDevelopmentGetter) => {
      if (!history.getSize()) return false;

      const newHistory = new LimitedStack(history);
      const changeToRevert = newHistory.pop();

      setHistory(newHistory);

      track('editor_change_reverted', {
        dynamic_page_id: changeToRevert?.campaignId,
        website_id: changeToRevert?.websiteId,
        reverted_update_type: changeToRevert?.type,
        history_depth: history.getSize(),
        has_before: !!changeToRevert?.before,
      });

      if (!changeToRevert?.before) return false;

      const after = JSON.parse(JSON.stringify(changeToRevert.before));
      applyChange(changeToRevert, after);

      const revertWebsiteUpdate = createWebsiteUpdate(
        changeToRevert.campaignId,
        changeToRevert.websiteId,
        changeToRevert.sectionId,
        changeToRevert.propKey,
        { after },
        'revertChange',
        changeToRevert.subitems,
        changeToRevert.before,
      );

      if (!revertWebsiteUpdate) return false;

      setSaveStatus(markSaving);
      enqueue(revertWebsiteUpdate);
      setRevertHistory((rh) => {
        const newHistory = new LimitedStack(rh);
        newHistory.push({ ...changeToRevert, stageOnCommit: undefined });
        return newHistory;
      });

      repaint(revertWebsiteUpdate, changeToRevert.before, getCurrent);

      const current = getCurrent?.(changeToRevert.websiteId);
      if (
        current &&
        revertTouched(current, changeToRevert.before, after, [
          'sections',
          'popups',
        ]).count > 0
      ) {
        show(
          'Part of this change was edited again since, so that part was kept.',
          'warn',
          { id: 'undo-conflict' },
        );
      }

      return true;
    },
    [history, createWebsiteUpdate, applyChange, enqueue, repaint, show],
  );

  const forwardRevert = useCallback(
    (getCurrent?: CurrentDevelopmentGetter) => {
      if (!revertHistory.getSize()) return false;

      const newHistory = new LimitedStack(revertHistory);
      const forwardChange = newHistory.pop();

      setRevertHistory(newHistory);

      track('editor_change_reapplied', {
        dynamic_page_id: forwardChange?.campaignId,
        website_id: forwardChange?.websiteId,
        reapplied_update_type: forwardChange?.type,
        redo_depth: revertHistory.getSize(),
      });

      if (!forwardChange?.before) return false;

      setSaveStatus(markSaving);
      enqueue(forwardChange);
      setHistory((h) => {
        const newHistory = new LimitedStack(h);
        newHistory.push(forwardChange);
        return newHistory;
      });

      repaint(forwardChange, forwardChange.before, getCurrent);

      return true;
    },
    [revertHistory, enqueue, repaint],
  );

  const _saveWebsite = useCallback(
    async (
      websiteId: string,
      websiteUpdates: WebsiteUpdate[],
      dynamicPageId: string,
    ) => {
      const websiteRef = doc(
        db,
        'accounts',
        websiteUpdates[0].accountId,
        'campaigns',
        dynamicPageId,
        'websites',
        websiteId,
      ).withConverter(emptyConverter<Website>());

      // A transaction, so a live write the agent lands between the read and
      // the write (SuperAgent applies draft-page edits directly) is re-read
      // and kept instead of being overwritten.
      const update = await runTransaction(db, async (transaction) => {
        const websiteDoc = await transaction.get(websiteRef);
        const development = websiteDoc.data()?.development;
        if (!websiteDoc.exists() || !development) return false;

        let changed = false;
        for (const wu of websiteUpdates) {
          changed = applyChange(wu, development) || changed;
        }
        websiteUpdates.forEach((wu) => wu.stageOnCommit?.(transaction));
        if (!changed) return false;

        // Compute formIds from the updated development data
        development.formIds = Array.from(getFormIdsInWebsiteData(development));
        // Compute CMS bindings so publish/live-render consumers can resolve
        // CMS data without re-scanning HTML.
        development.cmsRefs = getCMSRefsInWebsiteData(development);

        transaction.update(websiteRef, {
          development: removeUndefinedFieldsDeep(development) as any,
          updatedAt: Timestamp.now(),
        });
        // In the same transaction, so a retry never re-applies content that
        // already saved because only this flag failed.
        transaction.set(
          doc(
            db,
            'accounts',
            websiteUpdates[0].accountId,
            'campaigns',
            dynamicPageId,
          ),
          { hasChangesToPublish: true, lastEditedAt: Timestamp.now() },
          { merge: true },
        );
        return true;
      });

      if (update) {
        snapshotSavedEntity(
          websiteUpdates[0].accountId,
          `campaigns/${dynamicPageId}/websites/${websiteId}`,
          websiteUpdates,
        );
      }

      return { updated: update };
    },
    [applyChange, snapshotSavedEntity],
  );

  const _saveGlobalSection = useCallback(
    async (websiteId: string, websiteUpdates: WebsiteUpdate[]) => {
      const globalSectionId = websiteId.replace('globalSectionId_', '');
      const globalSectionRef = doc(
        db,
        'accounts',
        websiteUpdates[0].accountId,
        'globalSections',
        globalSectionId,
      ).withConverter(emptyConverter<GlobalSection>());

      const update = await runTransaction(db, async (transaction) => {
        const globalSectionDoc = await transaction.get(globalSectionRef);
        const sectionDevelopment = globalSectionDoc.data()?.development;
        if (!globalSectionDoc.exists() || !sectionDevelopment) return false;

        const simulatedDevelopment: WebsiteData = {
          sections: [sectionDevelopment],
        };

        let changed = false;
        for (const wu of websiteUpdates) {
          changed = applyChange(wu, simulatedDevelopment) || changed;
        }
        websiteUpdates.forEach((wu) => wu.stageOnCommit?.(transaction));
        if (!changed) return false;

        transaction.update(globalSectionRef, {
          development: removeUndefinedFieldsDeep(
            simulatedDevelopment.sections[0],
          ) as any,
          changesToPublish: true,
        });
        return true;
      });

      if (update) {
        snapshotSavedEntity(
          websiteUpdates[0].accountId,
          `globalSections/${globalSectionId}`,
          websiteUpdates,
        );
      }

      return { updated: update };
    },
    [applyChange, snapshotSavedEntity],
  );

  const pendingSinceRef = useRef<number | undefined>(undefined);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const retryAttemptRef = useRef(0);
  const retryNotBeforeRef = useRef(0);
  const wasOfflineRef = useRef(false);
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const savesInFlightRef = useRef(0);
  const saveWaitersRef = useRef(
    new Map<
      string,
      { resolve: () => void; reject: (error: unknown) => void }
    >(),
  );

  const settleWaiters = useCallback(
    (settled: WebsiteUpdate[], error?: unknown) => {
      for (const wu of settled) {
        const waiter = saveWaitersRef.current.get(wu.id);
        if (!waiter) continue;
        saveWaitersRef.current.delete(wu.id);
        if (error === undefined) waiter.resolve();
        else waiter.reject(error);
      }
    },
    [],
  );

  const waitForSaved = useCallback(
    (updateIds: string[]) =>
      Promise.all(
        updateIds.map(
          (id) =>
            new Promise<void>((resolve, reject) => {
              saveWaitersRef.current.set(id, { resolve, reject });
            }),
        ),
      ).then(() => undefined),
    [],
  );

  // Failed updates go back on the queue so the next attempt re-reads the
  // website and folds them again, instead of the edit being dropped.
  const saveQueuedBatch = useCallback(async () => {
    const _updates = updatesRef.current;
    if (!_updates.length) return;

    updatesRef.current = [];
    setUpdates((current) => current.filter((u) => !_updates.includes(u)));
    savesInFlightRef.current += 1;
    inFlightUpdatesRef.current = _updates;

    const failed: WebsiteUpdate[] = [];
    const failures = new Set<SaveFailure>();
    let lastError: unknown;
    const byDynamicPage = Object.entries(
      groupBy(_updates, (u) => u.campaignId),
    );

    for (const [dynamicPageId, campaignUpdates] of byDynamicPage) {
      const byWebsite = Object.entries(
        groupBy(campaignUpdates, (u) => u.websiteId),
      );
      const results = await Promise.allSettled(
        byWebsite.map(async ([websiteId, websiteUpdates]) => {
          const saveProperties = getUpdatesTrackingProperties(websiteUpdates);
          try {
            const result = websiteId.startsWith('globalSectionId_')
              ? await _saveGlobalSection(websiteId, websiteUpdates)
              : await _saveWebsite(websiteId, websiteUpdates, dynamicPageId);
            track('editor_autosave_success', {
              ...saveProperties,
              is_updated: result.updated,
            });
            return result;
          } catch (error) {
            track('editor_autosave_failed', {
              ...saveProperties,
              failure_reason:
                error instanceof Error ? error.message : String(error),
              failure_kind: classifySaveFailure(error),
              attempt_count: retryAttemptRef.current + 1,
            });
            throw error;
          }
        }),
      );

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') return;
        failed.push(...byWebsite[index][1]);
        lastError = result.reason;
        const failure = classifySaveFailure(result.reason);
        failures.add(failure);
        if (failure !== 'other' || retryAttemptRef.current > 0) return;
        Sentry.captureException(result.reason, {
          tags: { area: 'website-updater-save' },
          extra: { dynamicPageId },
        });
      });
    }

    savesInFlightRef.current -= 1;
    inFlightUpdatesRef.current = [];
    const savedAt = Date.now();
    recentlySavedRef.current.push(
      ..._updates
        .filter((u) => u.type === 'htmlElementChanged' && !failed.includes(u))
        .map((wu) => ({ wu, savedAt })),
    );
    settleWaiters(_updates.filter((u) => !failed.includes(u)));

    const saved = _updates.filter((u) => !failed.includes(u));
    if (saved.some((u) => u.conflicted && u.type === 'agentChange')) {
      show(
        "Part of SuperAgent's change overlapped text edited since, so that text was kept.",
        'warn',
        { id: 'agent-merge-conflict' },
      );
    }
    if (saved.some((u) => u.conflicted && u.type !== 'agentChange')) {
      show(
        'Someone changed the same text in the meantime, so your edit there was not applied.',
        'warn',
        { id: 'save-conflict' },
      );
    }

    if (!failed.length) {
      if (retryAttemptRef.current > 0) {
        track('editor_autosave_recovered', {
          failed_attempt_count: retryAttemptRef.current,
          was_offline: wasOfflineRef.current,
        });
      }
      retryAttemptRef.current = 0;
      retryNotBeforeRef.current = 0;
      wasOfflineRef.current = false;
      setSaveStatus('done');
      setLastSavedAt(new Date());
      return;
    }

    // A failed batch is never dropped: it goes back on the queue and is folded
    // again onto a fresh read. A write conflict clears once the other writer
    // stops, so it retries right away; anything else (offline, a rejected
    // write) backs off, and the editor says the changes are not saved yet.
    updatesRef.current = [...failed, ...updatesRef.current];
    setUpdates((current) => [...failed, ...current]);
    if (failures.size === 1 && failures.has('conflict')) {
      setSaveStatus('saving');
      return;
    }
    retryAttemptRef.current += 1;
    retryNotBeforeRef.current =
      Date.now() + getRetryDelay(retryAttemptRef.current);
    wasOfflineRef.current ||= failures.has('network');
    setSaveStatus(failures.has('network') ? 'offline' : 'error');
    // Whoever waits on these (an accept) is told now; the edits stay queued
    // and still save, with their commit hooks, once the write goes through.
    settleWaiters(failed, lastError);
  }, [_saveGlobalSection, _saveWebsite, settleWaiters, show]);

  // One batch at a time, so a retried batch can never land after a newer one.
  const savePendingUpdates = useCallback(() => {
    clearTimeout(saveTimerRef.current);
    pendingSinceRef.current = undefined;
    saveChainRef.current = saveChainRef.current.then(
      saveQueuedBatch,
      saveQueuedBatch,
    );
    return saveChainRef.current;
  }, [saveQueuedBatch]);

  useEffect(() => {
    if (!updates.length) return;

    pendingSinceRef.current ??= Date.now();
    saveTimerRef.current = setTimeout(
      savePendingUpdates,
      Math.max(
        getSaveDelay(pendingSinceRef.current),
        retryNotBeforeRef.current - Date.now(),
      ),
    );

    return () => {
      clearTimeout(saveTimerRef.current);
    };
  }, [savePendingUpdates, updates]);

  const retryNow = useCallback(() => {
    retryNotBeforeRef.current = 0;
    return savePendingUpdates();
  }, [savePendingUpdates]);

  useFlushSaves(
    retryNow,
    () => setSaveStatus('offline'),
    () => updatesRef.current.length > 0 || savesInFlightRef.current > 0,
  );

  /**
   * @deprecated Discard is no longer supported for websites. Changes are auto-saved.
   * This function is kept for backwards compatibility but will throw an error.
   */
  const _discardChangesWebsite = useCallback(
    async (_dynamicPageId: string, _websiteId: string) => {
      throw new Error(
        'Discard is no longer supported for websites. Changes are auto-saved.',
      );
    },
    [],
  );

  const _discardChangesGlobalSection = useCallback(
    async (dynamicPageId: string, websiteId: string) => {
      if (!selectedAccount) return;
      if (discarding) return;

      if (updates.some((wu) => wu.websiteId === websiteId)) {
        return;
      }

      setDiscarding(true);
      try {
        const globalSectionId = websiteId.replace('globalSectionId_', '');
        await captureSnapshot(
          selectedAccount.id,
          `globalSections/${globalSectionId}`,
          'autosave',
        );
        const globalSectionDoc = await getDoc(
          doc(
            db,
            'accounts',
            selectedAccount.id,
            'globalSections',
            globalSectionId,
          ).withConverter(emptyConverter<GlobalSection>()),
        );

        const development = globalSectionDoc.data()?.development;
        const production = globalSectionDoc.data()?.production;
        if (!globalSectionDoc.exists() || !production || !development) {
          throw new Error('This global section was not yet published');
        }

        const prodWebsiteData: WebsiteData = {
          sections: [production],
        };

        const developmentWebsiteData: WebsiteData = {
          sections: [development],
        };

        const discardWU = createWebsiteUpdate(
          dynamicPageId,
          websiteId,
          '',
          '',
          JSON.parse(JSON.stringify(prodWebsiteData)),
          'discard',
          undefined,
          developmentWebsiteData,
        );

        if (discardWU) {
          setHistory((h) => {
            const newHistory = new LimitedStack(h);
            newHistory.push(discardWU);

            return newHistory;
          });
        }
        setRevertHistory(
          (revertHistory) => new LimitedStack(revertHistory.getMaxLength()),
        );

        await updateDoc(globalSectionDoc.ref, {
          development: production as any,
          changesToPublish: false,
        });

        sendMessageToIframeByWebsiteId(
          {
            eventName: 'websiteUpdated',
            website: prodWebsiteData,
          },
          websiteId,
        );
      } catch (e) {
        Sentry.captureException(e);
        console.error(e);
        throw e;
      } finally {
        setDiscarding(false);
      }
    },
    [
      selectedAccount,
      discarding,
      updates,
      createWebsiteUpdate,
      captureSnapshot,
    ],
  );

  const discardChanges = useCallback(
    async (dynamicPageId: string, websiteId: string) => {
      if (websiteId.startsWith('globalSectionId_')) {
        return _discardChangesGlobalSection(dynamicPageId, websiteId);
      } else {
        return _discardChangesWebsite(dynamicPageId, websiteId);
      }
    },
    [_discardChangesGlobalSection, _discardChangesWebsite],
  );

  const _publishGlobalSection = useCallback(
    async (websiteId: string, globalSectionId: string) => {
      if (!selectedAccount) return;
      if (publishing) return;

      if (updates.some((wu) => wu.websiteId === websiteId)) {
        return;
      }

      auditLog.log(
        'update',
        'Publishing global section',
        'globalSection',
        globalSectionId,
        undefined,
        undefined,
        {},
      );

      track('publish_global_section', {
        globalSectionId,
        websiteId,
      });

      setPublishing(true);
      return publishGlobalSection(selectedAccount.id, globalSectionId).finally(
        () => {
          setPublishing(false);
        },
      );
    },
    [selectedAccount, publishing, updates, auditLog],
  );

  /**
   * Publish is now only used for GlobalSections.
   * Websites are auto-saved and no longer require publishing.
   */
  const publish = useCallback(
    async (_dynamicPageId: string, websiteId: string) => {
      if (websiteId.startsWith('globalSectionId_')) {
        const globalSectionId = websiteId.replace('globalSectionId_', '');
        setPublishGlobalSectionModalOpen({
          websiteId,
          globalSectionId,
          loading: false,
        });
        return;
      }
      // Websites are auto-saved, no publishing needed
    },
    [],
  );

  return (
    <WebsiteUpdaterContext.Provider
      value={{
        saveStatus,
        setSaveStatus,
        lastSavedAt,

        publishing,
        publish,

        discarding,
        discardChanges,

        createWebsiteUpdate,
        updateWebsite,

        canRevert: history.getSize() > 0,
        canForward: revertHistory.getSize() > 0,
        revertLatestUpdate,
        forwardRevert,
        clearHistory,

        applyChangesSync,

        flushSnapshots,
        flushPendingSaves: savePendingUpdates,
        waitForSaved,
      }}
    >
      {props.children}
      <AlertModal
        open={publishGlobalSectionModalOpen !== null}
        onClose={() => setPublishGlobalSectionModalOpen(null)}
        title="Publish Global Section"
        description="Are you sure you want to publish this global section? This will update all websites that use this section."
        actions={[
          {
            title: 'Cancel',
            onClick: () => setPublishGlobalSectionModalOpen(null),
            type: 'secondary',
          },
          {
            title: 'Publish',
            type: 'primary',
            color: 'primary',
            loading: publishGlobalSectionModalOpen?.loading,
            onClick: () => {
              const { websiteId, globalSectionId } =
                publishGlobalSectionModalOpen || {};
              if (!websiteId || !globalSectionId) return;
              setPublishGlobalSectionModalOpen((prev) =>
                prev
                  ? {
                      ...prev,
                      loading: true,
                    }
                  : null,
              );
              _publishGlobalSection(websiteId, globalSectionId)
                .then(() => {
                  setPublishGlobalSectionModalOpen(null);
                })
                .catch((e) => {
                  Sentry.captureException(e);
                  console.error(e);
                  setPublishGlobalSectionModalOpen((prev) =>
                    prev
                      ? {
                          ...prev,
                          loading: false,
                        }
                      : null,
                  );
                });
            },
          },
        ]}
        type={'warn'}
      />
    </WebsiteUpdaterContext.Provider>
  );
}
